/**
 * Slate Table block's View component.
 * @module volto-slate/blocks/Table/View
 */

import './public.less';

import React, { useMemo, useState } from 'react';
import {
  serializeNodes,
  serializeNodesToText,
} from '@plone/volto-slate/editor/render';
import cx from 'classnames';
import map from 'lodash/map';
import PropTypes from 'prop-types';
import { Table } from 'semantic-ui-react';
import { Node } from 'slate';

import { normalizeTableValue } from './imageUtils';
import useTopScrollMirror from './useTopScrollMirror';

const getAttributes = (node) =>
  node.textAlign ? { style: { textAlign: node.textAlign } } : undefined;

// Node.string so conta texto: uma celula com apenas uma imagem (sem texto)
// tem string vazia, mas ainda tem conteudo (o node `img`) que precisa ser
// renderizado - por isso tambem contamos os descendentes da arvore.
const cellHasContent = (value) =>
  Boolean(
    value &&
      (Node.string({ children: value }).length > 0 ||
        Array.from(Node.descendants({ children: value })).length > 2),
  );

const View = ({ data }) => {
  const [state, setState] = useState({
    column: null,
    direction: null,
  });

  const { containerRef, mirrorRef, mirrorContentRef } = useTopScrollMirror();

  const headers = useMemo(() => {
    return data.table.rows?.[0]?.cells;
  }, [data.table.rows]);

  const rows = useMemo(() => {
    const items = [];
    if (!data.table.rows) return [];
    data.table.rows.forEach((row, index) => {
      if (index > 0) {
        items[index] = [];
        row.cells.forEach((cell, cellIndex) => {
          items[index][cellIndex] = {
            ...cell,
            value: cellHasContent(cell.value)
              ? serializeNodes(normalizeTableValue(cell.value), getAttributes)
              : '\u00A0',
            valueText: cellHasContent(cell.value)
              ? serializeNodesToText(normalizeTableValue(cell.value))
              : '\u00A0',
          };
        });
      }
    });
    return items;
  }, [data.table.rows]);

  const sortedRows = useMemo(() => {
    if (state.column === null) return Object.keys(rows);
    return Object.keys(rows).sort((a, b) => {
      const a_text = rows[a][state.column].valueText;
      const b_text = rows[b][state.column].valueText;
      if (state.direction === 'ascending' ? a_text < b_text : a_text > b_text) {
        return -1;
      }
      if (state.direction === 'ascending' ? a_text > b_text : a_text < b_text) {
        return 1;
      }
      return 0;
    });
  }, [state, rows]);

  const handleSort = (index) => {
    if (!data.table.sortable) return;
    setState({
      column: index,
      direction:
        state.column !== index
          ? 'ascending'
          : state.direction === 'ascending'
            ? 'descending'
            : 'ascending',
    });
  };

  return (
    <div className="block slate-table">
      <div className="scroll-mirror-top" ref={mirrorRef} aria-hidden="true">
        <div ref={mirrorContentRef} />
      </div>
      <div
        className="block-inner-container"
        ref={containerRef}
        role="region"
        aria-label={data.table.caption || 'Tabela'}
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={0}
      >
        {data && data.table && (
          <Table
            fixed={data.table.fixed}
            compact={data.table.compact}
            basic={data.table.basic ? 'very' : false}
            celled={data.table.celled}
            inverted={data.table.inverted}
            striped={data.table.striped}
            sortable={data.table.sortable}
            className="slate-table-block"
            unstackable
          >
            {data.table.caption ? (
              <caption
                className={cx({
                  'text-up-01 text-weight-semi-bold text-left mb-2':
                    data.table.showCaption,
                  'sr-only': !data.table.showCaption,
                })}
              >
                {data.table.caption}
              </caption>
            ) : null}
            {!data.table.hideHeaders ? (
              <Table.Header>
                <Table.Row>
                  {headers.map((cell, index) => (
                    <Table.HeaderCell
                      key={cell.key}
                      scope="col"
                      textAlign="left"
                      verticalAlign="middle"
                      tabIndex={data.table.sortable ? '0' : '-1'}
                      sorted={state.column === index ? state.direction : null}
                      onClick={() => {
                        handleSort(index);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleSort(index);
                        }
                      }}
                      aria-sort={
                        state.column === index ? state.direction : 'none'
                      }
                    >
                      {cellHasContent(cell.value)
                        ? serializeNodes(
                            normalizeTableValue(cell.value),
                            getAttributes,
                          )
                        : '\u00A0'}
                    </Table.HeaderCell>
                  ))}
                </Table.Row>
              </Table.Header>
            ) : (
              ''
            )}
            <Table.Body>
              {map(sortedRows, (row) => (
                <Table.Row key={row}>
                  {map(rows[row], (cell) => (
                    <Table.Cell
                      key={cell.key}
                      textAlign="left"
                      verticalAlign="middle"
                    >
                      {cell.value}
                    </Table.Cell>
                  ))}
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </div>
    </div>
  );
};

View.propTypes = {
  data: PropTypes.objectOf(PropTypes.any).isRequired,
};

export default View;
